'use client';

import { useState } from 'react';
import Link from 'next/link';
import { ArrowRight } from 'lucide-react';
import s from '@/components/marketing/page/page.module.css';
import { cn } from '@/lib/utils';
import { formatDisplayDate } from '@/lib/utils/formatters';
import type { BlogPost } from './posts';

function PostMeta({ post, dark = false }: { post: BlogPost; dark?: boolean }) {
  return (
    <span
      className={cn(
        'flex flex-wrap gap-x-3 text-sm',
        dark ? 'text-white/50' : 'text-gray-500'
      )}
    >
      <time dateTime={post.published}>
        {formatDisplayDate(post.published, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })}
      </time>
      <span>{post.minutes} min read</span>
    </span>
  );
}

/** Featured post plus a category filter over the rest. */
export function BlogIndex({ posts }: { posts: readonly BlogPost[] }) {
  const [featured, ...rest] = posts;
  const categories = ['All', ...new Set(rest.map(post => post.category))];
  const [active, setActive] = useState('All');
  const visible =
    active === 'All' ? rest : rest.filter(post => post.category === active);

  return (
    <>
      {featured && (
        <Link
          href={`/blog/${featured.slug}`}
          className="group bg-umbra focus-visible:ring-accent-50 relative block overflow-hidden rounded-[28px] p-8 text-white outline-hidden focus-visible:ring-2 md:p-12"
        >
          <span
            aria-hidden="true"
            className="pointer-events-none absolute -top-40 -right-40 size-[520px] rounded-full bg-[radial-gradient(circle,color-mix(in_oklab,var(--accent-50)_22%,transparent),transparent_68%)] transition-transform duration-700 group-hover:scale-110"
          />
          <span className="relative flex max-w-2xl flex-col">
            <span className="text-accent-50 text-sm font-semibold">
              Featured · {featured.category}
            </span>
            <span className="mt-4 text-[clamp(1.75rem,1.2rem+2.2vw,2.75rem)] leading-[1.1] font-semibold tracking-[-0.03em]">
              {featured.title}
            </span>
            <span className="mt-4 text-lg leading-relaxed text-white/60">
              {featured.excerpt}
            </span>
            <span className="mt-8 flex flex-wrap items-center justify-between gap-4">
              <PostMeta post={featured} dark />
              <span className="flex items-center gap-1.5 font-medium">
                Read article
                <ArrowRight
                  className="size-4 transition-transform group-hover:translate-x-1"
                  aria-hidden="true"
                />
              </span>
            </span>
          </span>
        </Link>
      )}

      <div className="mt-16 flex flex-wrap items-end justify-between gap-4">
        <h2 className="text-[clamp(1.75rem,1.2rem+2vw,2.5rem)] font-semibold tracking-[-0.03em] text-gray-950">
          More articles
        </h2>
        <div
          role="group"
          aria-label="Filter by topic"
          className="flex flex-wrap gap-1.5"
        >
          {categories.map(category => (
            <button
              key={category}
              type="button"
              aria-pressed={active === category}
              onClick={() => setActive(category)}
              className={cn(
                s.segment,
                'focus-visible:ring-accent-50 bg-white px-4 py-2 text-sm font-medium text-gray-600 shadow-[0_0_0_1px_rgb(15_23_42/0.08)] outline-hidden hover:text-gray-950 focus-visible:ring-2'
              )}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      <div
        key={active}
        className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3"
        data-spotlight-group
      >
        {visible.map((post, index) => (
          <Link
            key={post.slug}
            href={`/blog/${post.slug}`}
            className={cn(
              s.card,
              s.fadeIn,
              'group focus-visible:ring-accent-50 flex flex-col p-6 outline-hidden focus-visible:ring-2'
            )}
            style={{ '--i': index } as React.CSSProperties}
            data-spotlight
          >
            <span className="text-sm font-semibold text-amber-700">
              {post.category}
            </span>
            <span className="mt-3 text-lg leading-snug font-semibold text-gray-950">
              {post.title}
            </span>
            <span className="mt-2 text-[15px] leading-relaxed text-gray-600">
              {post.excerpt}
            </span>
            <span className="mt-auto flex items-center justify-between pt-6">
              <PostMeta post={post} />
              <ArrowRight
                className="size-4 text-gray-400 transition-transform group-hover:translate-x-1 group-hover:text-gray-900"
                aria-hidden="true"
              />
            </span>
          </Link>
        ))}
      </div>
    </>
  );
}
